import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IAdjustmentCandidate, IAdjustmentListItem } from '../models/closing-adjustment.model';
import { CLOSING_ADJUSTMENT_FEATURE_KEY } from '../reducer/closing-adjustment.feature-key';
import { ClosingAdjustmentState } from '../reducer/closing-adjustment.reducer';
import {
    AdjustmentCandidate,
    AdjustmentFilterCounts,
    AdjustmentResultEffect,
    AdjustmentResultRows,
    AdjustmentRow,
    AdjustmentTransferOptions,
} from '../types/closing-adjustments.types';

const selectState = createFeatureSelector<ClosingAdjustmentState>(CLOSING_ADJUSTMENT_FEATURE_KEY);

/**
 * Wire item → the views' row type. A RENAME, not a computation.
 *
 * ⚠️ Nothing is DERIVED here — not the state, not the amount, not `editable`. The backend serves each
 * of them, because each is a rule that lives there: the state comes out of the journal (§2), the
 * amount is minor units all the way (`money-and-materiality.md`), and whether a correction may still
 * be edited is enforced in the backend rather than hidden in the UI (§7). A selector that computed
 * `editable: state === 'released'` would be a second copy of that rule, and only the backend's is enforced.
 */
/** The `P&L` column's sort weight — the sign of what the correction does to the result. */
const EFFECT_ORDER: Record<AdjustmentResultEffect, number> = {
    decrease: -1,
    none: 0,
    increase: 1,
    // Sorted BELOW every classified row, not with `none`. An unclassified booking is the one a user
    // has to act on — a missing account in the chart — so it belongs where it is findable rather than
    // mixed in with the rows that legitimately do not touch the result.
    excluded: -2,
};

function toRow(item: IAdjustmentListItem): AdjustmentRow {
    return {
        id: item.adjustmentId,
        state: item.state,
        source: item.source,
        text: item.text,
        date: item.date,
        debit: { id: item.debit.accountId, number: item.debit.accountNumber, name: item.debit.name },
        credit: { id: item.credit.accountId, number: item.credit.accountNumber, name: item.credit.name },
        owner:
            item.owner === null
                ? null
                : { id: item.owner.accountId, number: item.owner.accountNumber, name: item.owner.name },
        amount: { minor: item.amountMinor, currency: item.currency },
        // Passed through, never recomputed: relevance comes from the mandate's account group and the
        // sign from the posting side, and both were already decided where the totals are decided.
        resultEffect: item.resultEffect,
        vatCode: item.vatCode,
        remark: item.remark,
        ambiguous: item.ambiguous,
        notFound: item.notFound,
        discarded: item.discarded,
        enteredBy: item.enteredBy,
        editedBy: item.editedBy,
        editable: item.editable,
    };
}

function toCandidate(candidate: IAdjustmentCandidate): AdjustmentCandidate {
    return {
        journalEntryId: candidate.journalEntryId,
        date: candidate.date,
        description: candidate.description,
        amount: { minor: candidate.amountMinor, currency: candidate.currency },
        documentNumber: candidate.documentNumber,
    };
}

/**
 * The rows the closing-wide register renders — SEARCHED and SORTED here.
 *
 * ⚠️ **This is the one place in the flow where the client does narrow a list, and it is deliberate.**
 * The document hub sends its search to the server because the server has to recompute the pill counts
 * over the term; here the four counts are a property of the CLOSING-YEAR and do not move with a search
 * (a count that did would always equal the visible row count). And the list is one fiscal year of one
 * closing — a handful of rows — so a round trip per keystroke would buy nothing.
 *
 * The FILTER is not applied here: it is sent to the server, because which rows exist for a pill is the
 * server's decision and the counts have to agree with them.
 */
export const selectAdjustmentRows = createSelector(selectState, (state): AdjustmentRow[] => {
    const rows = state.adjustments.map(toRow);
    const searched = applySearch(rows, state.searchTerm);

    return applySort(searched, state);
});

/** How many rows the search matched. `null` when nothing is being searched — a different state. */
export const selectAdjustmentSearchHits = createSelector(selectState, (state): number | null => {
    if (state.searchTerm.trim().length === 0) {
        return null;
    }

    return applySearch(state.adjustments.map(toRow), state.searchTerm).length;
});

/**
 * The four pill counts, straight from the server.
 *
 * One per state plus `all`, so the numbers add up. `transferred` used to be dropped here on the
 * grounds that the surface had three pills; the prototype has four, and hiding the count of the state
 * the user is actively waiting on was the wrong half of that trade.
 */
export const selectAdjustmentCounts = createSelector(
    selectState,
    (state): AdjustmentFilterCounts => ({
        all: state.counts.all,
        released: state.counts.released,
        transferred: state.counts.transferred,
        booked: state.counts.booked,
        // Outside `all` on purpose — `all === released + transferred + booked` has to keep holding,
        // and a withdrawn correction is not a fourth rung of the ladder.
        discarded: state.counts.discarded,
    }),
);

export const selectAdjustmentActiveFilter = createSelector(selectState, (state) => state.activeFilter);
export const selectAdjustmentSort = createSelector(selectState, (state) => state.sort);
export const selectAdjustmentSearchTerm = createSelector(selectState, (state) => state.searchTerm);
export const selectAdjustmentFiscalYears = createSelector(selectState, (state) => state.availableFiscalYears);

/** The closing's balance-sheet date — the entry dialog's default booking date. `null` until loaded. */
export const selectAdjustmentBalanceSheetDate = createSelector(selectState, (state) =>
    state.balanceSheetDate === '' ? null : state.balanceSheetDate,
);
export const selectAdjustmentSelectedYear = createSelector(selectState, (state) => state.selectedFiscalYear);
export const selectAdjustmentReadOnly = createSelector(selectState, (state) => state.readOnly);
export const selectAdjustmentsLoading = createSelector(selectState, (state) => state.loading);
export const selectAdjustmentsError = createSelector(selectState, (state) => state.error);

/** The three derived figures, renamed for the view. `null` until the first load. */
export const selectAdjustmentResult = createSelector(selectState, (state): AdjustmentResultRows | null => {
    const result = state.result;
    if (result === null) {
        return null;
    }

    return {
        currency: result.currency,
        provisionalMinor: result.provisionalResultMinor,
        impactMinor: result.resultImpactMinor,
        resultAfterMinor: result.resultAfterAdjustmentsMinor,
        totalMinor: result.adjustmentTotalMinor,
        unclassifiedCount: result.unclassifiedAdjustments,
    };
});

/**
 * What the transfer control may offer.
 *
 * ⚠️ Only the NAME of the connected system travels to the view, never the vendor enum: a vendor code is
 * a code, and codes do not belong in a user-facing sentence (§3a rule 4).
 */
export const selectAdjustmentTransferOptions = createSelector(
    selectState,
    (state): AdjustmentTransferOptions => ({
        canWriteToErp: state.transferOptions.canWriteToErp,
        reasonKey: state.transferOptions.reasonKey,
        connectedName: state.transferOptions.connected?.displayName ?? null,
    }),
);

// ── The detail route ─────────────────────────────────────────────────────────────────────────────

export const selectAdjustmentDetail = createSelector(selectState, (state): AdjustmentRow | null =>
    state.adjustment === null ? null : toRow(state.adjustment),
);
export const selectAdjustmentCandidates = createSelector(selectState, (state): AdjustmentCandidate[] =>
    state.candidates.map(toCandidate),
);
export const selectAdjustmentDetailLoading = createSelector(selectState, (state) => state.adjustmentLoading);
export const selectAdjustmentDetailError = createSelector(selectState, (state) => state.adjustmentError);

// ── Writes ───────────────────────────────────────────────────────────────────────────────────────

export const selectAdjustmentWritePending = createSelector(selectState, (state) => state.writePending);
export const selectAdjustmentWriteErrorKey = createSelector(selectState, (state) => state.writeErrorKey);
export const selectAdjustmentLastTransfer = createSelector(selectState, (state) => state.lastTransfer);

/**
 * The ids the transfer control sends: every OPEN correction of the year, from the SERVER.
 *
 * ⚠️ Read from its own field, never derived from `state.adjustments`. That array is the filtered page
 * — the controller narrows the rows to the active pill's state at the edge — so deriving the set from
 * it meant that while the user was looking at `Gebucht` there were no open rows to find, the transfer
 * set came out empty, and `Übertragen` did NOTHING while still showing a non-zero count. The count
 * and the ids now come from the same unfiltered set, which is the only way the two can agree.
 *
 * The search is client-side and deliberately does not narrow this either: transferring only the rows
 * someone had typed a filter down to would be a surprise with money in it.
 */
export const selectTransferableAdjustmentIds = createSelector(
    selectState,
    (state): string[] => state.transferableAdjustmentIds,
);

/**
 * The search: booking text, account number, account name and amount (AC3).
 *
 * The AMOUNT is matched on its rendered decimal form, because that is what the user sees and therefore
 * what they type — searching the minor-unit integer would mean `4500` finds `CHF 45.00`, which nobody
 * expects.
 */
function applySearch(rows: AdjustmentRow[], term: string): AdjustmentRow[] {
    const needle = term.trim().toLowerCase();
    if (needle.length === 0) {
        return rows;
    }

    return rows.filter((row) => {
        const amount = (row.amount.minor / 100).toFixed(2);
        const haystack = [
            row.text,
            row.debit.number,
            row.debit.name,
            row.credit.number,
            row.credit.name,
            row.owner?.number ?? '',
            row.owner?.name ?? '',
            amount,
        ]
            .join(' ')
            .toLowerCase();

        return haystack.includes(needle);
    });
}

/** The six sortable columns. `null` leaves the server's order — newest first. */
function applySort(rows: AdjustmentRow[], state: ClosingAdjustmentState): AdjustmentRow[] {
    const sort = state.sort;
    if (sort === null) {
        return rows;
    }

    const direction = sort.direction === 'asc' ? 1 : -1;
    // A COPY: a selector must not mutate the array it was handed, or the store's own reference stops
    // being a reliable change signal.
    return [...rows].sort((left, right) => {
        switch (sort.key) {
            case 'state':
                return direction * left.state.localeCompare(right.state);
            case 'text':
                return direction * left.text.localeCompare(right.text);
            case 'debit':
                return direction * left.debit.number.localeCompare(right.debit.number);
            case 'credit':
                return direction * left.credit.number.localeCompare(right.credit.number);
            case 'amount':
                return direction * (left.amount.minor - right.amount.minor);
            case 'source':
                return direction * left.source.localeCompare(right.source);
            case 'pnl':
                // By the SIGN, so the column sorts the way the figures read: everything that lowers
                // the result, then everything that leaves it alone, then everything that raises it.
                // Alphabetical on the label would order it by language instead.
                return direction * (EFFECT_ORDER[left.resultEffect] - EFFECT_ORDER[right.resultEffect]);
        }
    });
}

// ── The IN-ACCOUNT list (REV-21364) ─────────────────────────────────────────────────────────────
//
// Its own selectors over its own branch. Deliberately NOT reusing the register's: the two lists are
// different questions about different scopes, and one set of selectors over one array is how they
// would start overwriting each other.

const selectAccountBranch = createSelector(selectState, (state) => state.account);

export const selectAccountAdjustmentRows = createSelector(selectAccountBranch, (account): AdjustmentRow[] =>
    account.adjustments.map(toRow),
);

export const selectAccountAdjustmentYears = createSelector(
    selectAccountBranch,
    (account): number[] => account.availableFiscalYears,
);

export const selectAccountAdjustmentSelectedYear = createSelector(
    selectAccountBranch,
    (account): number | null => account.selectedFiscalYear,
);

export const selectAccountAdjustmentReadOnly = createSelector(
    selectAccountBranch,
    (account): boolean => account.readOnly,
);

/** `n offen`. The view drops the badge at 0 — a count of nothing is not a fact worth a pill. */
export const selectAccountAdjustmentOpenCount = createSelector(
    selectAccountBranch,
    (account): number => account.openCount,
);

export const selectAccountAdjustmentsLoading = createSelector(
    selectAccountBranch,
    (account): boolean => account.loading,
);

export const selectAccountAdjustmentsError = createSelector(
    selectAccountBranch,
    (account): boolean => account.error,
);
